import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toastError, toastSuccess } from "@/shared/lib/toast";
import { usePhysicianAuthStore } from "@/stores/physicianAuthStore";
import { AuthTextField, AuthPasswordField } from "@/shared/components/AuthField";
import { AuthSubmitButton } from "@/shared/components/AuthSubmitButton";
import { validateFields } from "@/shared/lib/validators";

export const REGISTERED_BANNER = "Your account is ready. Please sign in.";
export const INVALID_CREDENTIALS_MESSAGE = "Incorrect email or password.";

// Invite-only portal: accounts are created from an admin's invitation link
// (/physician/invite/:token), so there is deliberately no signup link here.
export function PhysicianLoginPage() {
  const signIn = usePhysicianAuthStore((s) => s.signIn);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Same pattern as PatientSignInPage's post-verify notice: toast once (the ref
  // guards StrictMode's double effect run), then drop the param so a refresh or
  // back/forward can't replay it.
  const noticeShownRef = useRef(false);
  useEffect(() => {
    if (noticeShownRef.current || searchParams.get("registered") !== "1") return;
    noticeShownRef.current = true;
    toastSuccess(REGISTERED_BANNER);
    navigate("/physician/login", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const fieldErrors = validateFields(
      { email, password },
      {
        email: [
          { required: true, message: "Email is required" },
          { type: "email", message: "Enter a valid email address" },
        ],
        password: [{ required: true, message: "Password is required" }],
      },
    );
    setErrors(fieldErrors);
    if (Object.keys(fieldErrors).length > 0) return;

    setLoading(true);
    const result = await signIn(email, password);
    setLoading(false);

    if (result.success) {
      navigate("/physician", { replace: true });
      return;
    }
    toastError(result.code === "INVALID_CREDENTIALS" ? INVALID_CREDENTIALS_MESSAGE : result.error ?? "Unable to sign in.");
  };

  return (
    <div className="w-full max-w-sm">
      <h2 className="!font-normal text-[28px] leading-tight text-[#0A2947]">
        Welcome To <span className="font-extrabold tracking-tight">MedSyncIQ</span>
      </h2>
      <p className="!mt-2 !mb-9 text-sm text-[#9CA3AF]">Sign in to the physician portal</p>

      <form onSubmit={handleSubmit} noValidate>
        <AuthTextField
          label="Email"
          placeholder="Enter your email"
          autoComplete="email"
          value={email}
          onChange={(v) => { setEmail(v); setErrors((e) => ({ ...e, email: "" })); }}
          error={errors.email}
        />
        <AuthPasswordField
          label="Password"
          placeholder="Enter your password"
          autoComplete="current-password"
          value={password}
          onChange={(v) => { setPassword(v); setErrors((e) => ({ ...e, password: "" })); }}
          error={errors.password}
          className="mb-7"
        />
        <AuthSubmitButton loading={loading}>Sign in</AuthSubmitButton>
      </form>

      <p className="!mt-8 text-center text-sm text-[#9CA3AF]">
        Physician accounts are created by invitation from administrator of MedSyncIQ
      </p>
    </div>
  );
}
